/**
 * Local post-processing for browser speech-to-text (no API / no AI Usage Credits).
 * Tuned for Indian English + portfolio money phrases.
 */

const SPOKEN_ONES = {
  zero: 0,
  one: 1,
  two: 2,
  three: 3,
  four: 4,
  five: 5,
  six: 6,
  seven: 7,
  eight: 8,
  nine: 9,
  ten: 10,
  eleven: 11,
  twelve: 12,
  thirteen: 13,
  fourteen: 14,
  fifteen: 15,
  sixteen: 16,
  seventeen: 17,
  eighteen: 18,
  nineteen: 19,
};

const SPOKEN_TENS = {
  twenty: 20,
  thirty: 30,
  forty: 40,
  fifty: 50,
  sixty: 60,
  seventy: 70,
  eighty: 80,
  ninety: 90,
};

/** Word-boundary fixes for common Chrome/Edge mis-hearings in finance dictation. */
const PHRASE_FIXES = [
  [/\bratio\b/gi, "rupee"],
  [/\brupay\b/gi, "rupee"],
  [/\brupies\b/gi, "rupees"],
  [/\brs\.?\b/gi, "rupees"],
  [/\binr\b/gi, "INR"],
  [/\black\b/gi, "lakh"],
  [/\blac\b/gi, "lakh"],
  [/\blaks\b/gi, "lakhs"],
  [/\bcredit\s+card\b/gi, "credit card"],
  [/\bh\s*d\s*f\s*c\b/gi, "HDFC"],
  [/\bs\s*b\s*i\b/gi, "SBI"],
  [/\bi\s+c\s*i\s+c\s+i\b/gi, "ICICI"],
  [/\bixigi\b/gi, "ICICI"],
  [/\bupi\b/gi, "UPI"],
  [/\bfd\b/gi, "FD"],
  [/\bcash\s+in\b/gi, "cash in"],
  [/\bcash\s+out\b/gi, "cash out"],
  [/\b14\s+th\b/gi, "14th"],
  [/\b15\s+th\b/gi, "15th"],
];

function normalizeSpaces(s) {
  return String(s || "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * If the whole utterance is spoken-number words, convert to digits (best-effort).
 */
export function tryParseSpokenIndianAmount(text) {
  const raw = normalizeSpaces(text).toLowerCase();
  if (!raw || /\d/.test(raw)) return null;
  if (!/\b(lakh|lakhs|thousand|hundred|crore|rupee|rupees)\b/.test(raw)) {
    return null;
  }

  const tokens = raw.replace(/,/g, "").split(/\s+/).filter(Boolean);
  let total = 0;
  let current = 0;

  const flush = () => {
    total += current;
    current = 0;
  };

  for (let i = 0; i < tokens.length; i++) {
    const t = tokens[i];
    if (t in SPOKEN_ONES) {
      current += SPOKEN_ONES[t];
      continue;
    }
    if (t in SPOKEN_TENS) {
      current += SPOKEN_TENS[t];
      continue;
    }
    if (t === "hundred") {
      current = (current || 1) * 100;
      continue;
    }
    if (t === "thousand") {
      current = (current || 1) * 1000;
      continue;
    }
    if (t === "lakh" || t === "lakhs") {
      current = (current || 1) * 100000;
      flush();
      continue;
    }
    if (t === "crore" || t === "crores") {
      current = (current || 1) * 10000000;
      flush();
      continue;
    }
    if (t === "and") continue;
    if (/^rupees?$/.test(t)) continue;
    return null;
  }
  flush();
  if (!Number.isFinite(total) || total <= 0) return null;
  return String(Math.round(total));
}

/**
 * @param {string} text
 * @param {{ categoryNames?: string[] }} [opts]
 */
export function cleanupPortfolioSpeechTranscript(text, opts = {}) {
  let s = normalizeSpaces(text);
  if (!s) return "";

  for (const [re, repl] of PHRASE_FIXES) {
    s = s.replace(re, repl);
  }

  const categoryNames = opts.categoryNames || [];
  for (const name of categoryNames) {
    const n = String(name || "").trim();
    if (n.length < 3) continue;
    const escaped = n.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\b${escaped}\\b`, "gi");
    if (!re.test(s)) {
      const loose = new RegExp(
        `\\b${escaped.replace(/\s+/g, "\\s+")}\\b`,
        "gi"
      );
      if (loose.test(s)) {
        s = s.replace(loose, n);
      }
    }
  }

  return normalizeSpaces(s);
}

export const PORTFOLIO_STT_LANG_OPTIONS = [
  { value: "en-IN", label: "English (India) — recommended" },
  { value: "en-US", label: "English (US)" },
  { value: "en-GB", label: "English (UK)" },
  { value: "hi-IN", label: "Hindi (India)" },
];

export function setPreferredSttLang(lang) {
  try {
    localStorage.setItem("manage360_speech_lang", String(lang || "en-IN").trim());
  } catch {
    /* ignore */
  }
}
